import React from 'react';
import { Link } from 'react-router-dom';
import { Icon, Tooltip } from '@patternfly/react-core';
import { ClusterIcon, TagIcon, VirtualMachineIcon } from '@patternfly/react-icons';
import type { SplaSubjectType } from '../../types/models/Spla';

// Shared SPLA subject rendering — used by the staff /admin/spla tabs AND the tenant Licenses page,
// so both surfaces show a subject (a VM, a bare-metal server, a manual name) the same way.

export const SPLA_SUBJECT_TYPE_LABELS: Record<SplaSubjectType, string> = {
  virtual_machine: 'Virtual machine',
  device: 'Server',
  manual: 'Manual',
};

/**
 * Detail-page path for a typed subject (`ref` is its numeric NetBox id), or `null` for `manual`
 * subjects (a staff-chosen stable name links nowhere).
 */
export function splaSubjectLinkPath(type: SplaSubjectType, ref: string): string | null {
  switch (type) {
    case 'virtual_machine':
      return `/datacenter/virtual-machines/${ref}`;
    case 'device':
      return `/datacenter/servers/${ref}`;
    default:
      return null;
  }
}

function subjectIcon(type: SplaSubjectType): React.ReactElement {
  switch (type) {
    case 'virtual_machine':
      return <VirtualMachineIcon />;
    case 'device':
      return <ClusterIcon />;
    default:
      return <TagIcon />;
  }
}

interface SplaSubjectCellProps {
  type: SplaSubjectType;
  /** The subject's `ref` (NetBox id for typed subjects, stable name for `manual`). */
  subjectRef: string;
  /** Display name (`label ?? ref` — precomputed by the backend where available). */
  name: string;
}

/**
 * Icon + name for one SPLA subject; typed subjects (VM / server) link to their detail page. The
 * icon carries the type for screen readers and shows it on hover.
 */
export function SplaSubjectCell({ type, subjectRef, name }: SplaSubjectCellProps) {
  const path = splaSubjectLinkPath(type, subjectRef);
  const typeLabel = SPLA_SUBJECT_TYPE_LABELS[type];
  return (
    <>
      <Tooltip content={typeLabel}>
        <Icon size="sm" aria-label={typeLabel} style={{ marginRight: '6px' }}>
          {subjectIcon(type)}
        </Icon>
      </Tooltip>
      {path !== null ? <Link to={path}>{name}</Link> : name}
    </>
  );
}
